import React, { useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { canViewUserReport, isCompanyAdminUser } from "@shared/utils/AccessControl";
import { useTranslation } from "react-i18next";
import { getAllWorkGroupsWhereaboutsSummary, getWorkgroupsWhereaboutsSummary } from '@features/reporting/services/ReportingService';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { MountedContentCard, MountedContentCardContent } from '@shared/ui/atoms/MountedContentCard';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { SpaceBetweenRow } from '@features/settings/pages/StyleComponents';
import Colours from '@shared/ui/atoms/Colours';
import { WhereaboutsSummaryBarChart } from "./components/WhereaboutsSummaryBarChart";
import { Spacer } from '@shared/ui/atoms/VerticalSpacer';
import moment, { Moment } from "moment";
import { calculateMaxValue, transformWhereaboutsUsage } from "./WhereaboutsReportingPage";
import { selectAllTags } from "@shared/store/userTags.duck";
import { WhereaboutsBookingDownloadReportBar } from "./components/WhereaboutsBookingDownloadReportBar";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { REACT_SELECT_DROPDOWN_STYLES } from '@shared/ui/organisms/OfficeDropDownList';

const animatedComponents: any = makeAnimated();

const ALL_WORKGROUPS = -1;

export function WorkGroupWhereaboutsReport() {
  const [selectedWorkGroupId, setSelectedWorkGroupId] = useState<number>(0);
  const workgroups = useSelector(selectAllTags);
  const [date, setDate] = useState<Moment>(moment());

  const [reportData, setReportData] = useState<any>([]);
  const [keyTableData, setKeyTableData] = useState<any>([]);
  const [maxYAxisValue, setMaxYAxisValue] = useState(0);

  const [loading, setLoading] = useState(false);
  const currentUser = useSelector(selectCurrentUser);
  const hasAccess = useMemo(() => canViewUserReport(currentUser),[currentUser]);
  const {t} = useTranslation();

  const canViewAllWorkGroups = useMemo(() => isCompanyAdminUser(currentUser), [currentUser]);
  const isAllWorkGroupsSelected = selectedWorkGroupId === ALL_WORKGROUPS;

  const workGroupOptions = useMemo(() => {
    const options = workgroups.map(w => ({ value: w.id, label: w.label }));
    if (canViewAllWorkGroups) {
      return [{ value: ALL_WORKGROUPS, label: t('reports-page.all-workgroups') }, ...options];
    }
    return options;
  }, [workgroups, canViewAllWorkGroups, t]);

  useEffect(() => {
    if (canViewAllWorkGroups) {
      setSelectedWorkGroupId(ALL_WORKGROUPS);
    }
  }, [canViewAllWorkGroups]);

  const onWorkGroupSelected = (workGroupId?: number) => {
    setReportData([]);
    if (workGroupId) {
      setSelectedWorkGroupId(workGroupId);
    }
  }

  useEffect(() => {
    if (date && selectedWorkGroupId) {
      setReportData([]);
      setLoading(true)
      const summaryRequest = isAllWorkGroupsSelected
        ? getAllWorkGroupsWhereaboutsSummary(date)
        : getWorkgroupsWhereaboutsSummary(date, selectedWorkGroupId);
      summaryRequest.then(resp => {
        const optionList = resp.whereaboutsOptionList;
        const whereaboutsMonthlySummaryKeys = Object.keys(resp.whereaboutsUsage);
        const data = whereaboutsMonthlySummaryKeys.map(key => ({
          name: moment(key).format('ddd')[0],
          dateOfMonth: moment(key).format('DD'),
          date: key,
          // @ts-ignore
          ...transformWhereaboutsUsage(optionList, resp.whereaboutsUsage[key])
        }))
        data.sort((a, b) => a.date < b.date ? -1 : 1);
        setReportData(data);
        setKeyTableData(optionList);
        setMaxYAxisValue(calculateMaxValue(resp.whereaboutsUsage));
      }).finally(() => {
        setLoading(false);
      })
    }
  }, [date, selectedWorkGroupId]);

  const onDateChange = (date: Moment) => {
    setDate(date);
  }


  return (
    <ReportingContainer>
      {hasAccess &&
        <>

          <HeadlineLarge style={{marginBottom: 16}}>{t('reports-page.whereabouts-title')}</HeadlineLarge>

          <SpaceBetweenRow>
            <DateFlicker dateScale={DateScale.Month}
                         updateGlobalDate={false}
                         onChange={onDateChange}
                         disabled={loading} />
            <div style={{width: 230, maxWidth: '100%'}}>
              <Select
                options={workGroupOptions}
                onChange={(item: any) => onWorkGroupSelected(item?.value)}
                value={workGroupOptions.find(o => o.value === selectedWorkGroupId) ?? null}
                closeMenuOnSelect={true}
                styles={REACT_SELECT_DROPDOWN_STYLES}
                components={{ ...animatedComponents, ClearIndicator: () => null, IndicatorSeparator: () => null }}
                isMulti={false}
                isDisabled={loading}
                placeholder={t('Select...')}
                className="teamDropDownList"
              />
            </div>
          </SpaceBetweenRow>
          {!!selectedWorkGroupId ? <>
            {loading ? <>
              <Center>
                <LoadingSpinner hideBorder={true} />
              </Center>
            </> : <>
              {reportData.length === 0 ? <>
                <Center style={{marginTop: 32}}>
                  <MountedContentCard>
                    <MountedContentCardContent>
                      <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 12}}>{t('reports-page.whereabouts-report.no-date')}</BodyLarge>
                      <BodyRegular>{t('reports-page.whereabouts-report.select-a-date')}</BodyRegular>
                    </MountedContentCardContent>
                  </MountedContentCard>
                </Center>
              </> : <>
                <WhereaboutsSummaryBarChart reportData={reportData}
                                            maxValueOfYAxis={maxYAxisValue}
                                            keyTableData={keyTableData} />
              </>}
            </>}
            <Spacer />
            <WhereaboutsBookingDownloadReportBar selectedWorkGroupId={isAllWorkGroupsSelected ? undefined : selectedWorkGroupId}
                                                 selectedWorkGroupName={isAllWorkGroupsSelected ? t('reports-page.all-workgroups') : workgroups.find(w => w.id === selectedWorkGroupId)?.label}
                                                 allWorkGroups={isAllWorkGroupsSelected} />
          </> : <>
          </>}
        </>
      }
    </ReportingContainer>
  );
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
`
